/**
 * Modes redirect map (UI-rewrite, Impl. Plan §2; IA §6 route philosophy). The
 * authoritative old-route → mode-route resolver that guarantees the rewrite's
 * coverage rule: **every legacy surface either migrates into a mode or redirects
 * into one — no 404, no orphan.** Mirrors streams/redirects.ts: PURE substrate
 * (string → mode destination), consumed by the root layout's `redirect()` stub +
 * the nav chrome. Live since the P6 atomic flip (the NEXT_PUBLIC_MODES_NAV flag
 * is retired; modes is unconditional with no streams-nav fallback).
 *
 * Route shape (IA §6): a URL is a projection request — `/<mode>` (workspace tier)
 * or `/projects/<p>/<mode>` (scoped tier), `?view=dashboard|queue|objects|
 * analytics`. Deep legacy sub-routes are preserved losslessly as `?sub=<rest>`
 * for the mode page to resolve (the same deferred-decision discipline streams
 * used) — so no deep link breaks.
 *
 * Tools and dev/QA surfaces (settings, database, help, search, login, onboarding,
 * styleguide, skeletons, doc-render, products mount) are NOT modes (Screen
 * Inventory §4.3/§4.4) and deliberately resolve to `null` — they keep their own
 * routes under the modes nav.
 */

import { type StreamSlug } from "@excellent/core";
import {
  SECTION_MODE,
  STREAM_MODE,
  isModeSlug,
  modePath,
  type ModeSlug,
  type ModeView,
} from "./modes-registry";

export interface ModeRedirect {
  mode: ModeSlug;
  /** Scoped tier when set; workspace tier when null. */
  projectSlug: string | null;
  view?: ModeView;
  /** Query to append (preserved deep `sub`, sub-area `area`, inbox flag, …). */
  extra: Record<string, string>;
}

/**
 * Top-level core surfaces → mode. These are the SITE-MAP "core" + scattered
 * utilities that fold into a mode (Screen Inventory §5). Each carries a default
 * view + any sub-area marker the mode page reads.
 */
const CORE_ROUTE: Readonly<
  Record<string, { mode: ModeSlug; view?: ModeView; area?: string; sub?: string }>
> = {
  // Work absorbs Issues · Planner · Inbox (suggestions/activity) · Reviews.
  // After the Work-mode consolidation (PR-1) these fold into the single Issues
  // domain (slug `queue`) as routed `?sub` views. 3.B: `/verdicts` is no longer
  // the Issues reviews tab — it is the canonical review door at
  // `/agents/verification` (C3). Inbox alias still folds into Issues. NB:
  // `notifications` is deliberately NOT here — it is a first-class standalone
  // page (the Notification Center), NOT the agent decision inbox. It resolves
  // to null (NON_MODE_ROOTS) so its real page renders.
  issues: { mode: "work", area: "queue" },
  tasks: { mode: "work", area: "queue" },
  planner: { mode: "work", area: "queue", sub: "planner" },
  verdicts: { mode: "agents", area: "verification" },
  suggestions: { mode: "work", view: "queue" },
  activity: { mode: "work" },
  // NB: `/favorites` is deliberately NOT here. It is a first-class standalone
  // page (excellent/app/favorites — PageHeader + the starred-pages list), not a
  // Work Domain. Folding it as `?area=favorites` dead-ended: Work has no
  // favorites Domain, so the section route notFound()'d it. It now resolves to
  // null (see NON_MODE_ROOTS) so its real page renders; the legacy
  // `/work?area=favorites` form is forwarded back to it in app/layout.tsx.
  // Agents mode (already lives at /agents — resolves to itself, P4)
  agents: { mode: "agents" },
  // Personal UI root folds into Work ▸ Issues. The data layer remains exempt
  // below; this is a hide, not a teardown.
  personal: { mode: "work", area: "queue" },
  // Operations gathers the back-office spine
  finance: { mode: "operations", area: "finance" },
  // Directory folds into the Operations ▸ Team domain (which hosts the
  // people/orgs/activity directory view) — its area is `team`, not `directory`
  // (there is no `directory` domain, which would dead-end on the generic table).
  directory: { mode: "operations", area: "team" },
  team: { mode: "operations", area: "team" },
  audits: { mode: "operations", area: "audits" },
  // /market used to fold into hidden customers/market. Re-point to Work ▸ Issues
  // so old bookmarks do not land on a hidden surface; the market hub still
  // mounts at /customers?area=market for direct deep links.
  market: { mode: "work", area: "queue" },
};

/**
 * Folded product roots → { mode, area }. These already redirect to section routes
 * today; the modes layer maps them straight to the owning mode + its Domain
 * (`area`) so a deep product link lands in the real Layer-2 app, never a generic
 * table. `hiring` folds into Operations as its Hiring Domain (2026-06-17, reverting
 * the brief LOCKED #2 promotion to its own mode).
 */
const PRODUCT_ROUTE: Readonly<Record<string, { mode: ModeSlug; area: string }>> = {
  hiring: { mode: "operations", area: "hiring" },
  crm: { mode: "customers", area: "sales" },
  lens: { mode: "agents", area: "lens" },
  // `document` was extracted into the standalone @excellent-so/selfdocs app
  // (#43475) — no in-shell surface remains, so the root no longer redirects.
};

/**
 * The public Hiring careers route shape — `/hiring/jobs/<slug>` for an `is_public`
 * job (NOT `/careers/*`, NOT `?public=1`; those routes do not exist) — plus the
 * hiring API subtree. These are EXEMPT from the modes redirect (Three-Layer PRD
 * §3.3.2): an external applicant must reach the public apply form, never the
 * internal commitment table. The layout redirect guards on this, and
 * `resolveModeRedirect` returns `null` for them as a second backstop so
 * `modeHrefFor` never rewrites them in nav.
 */
const PUBLIC_HIRING_RE = /^\/hiring\/jobs\/[^/]+\/?$/;
export function isPublicHiringPath(pathname: string): boolean {
  const path = pathname.split("?")[0] ?? "";
  return PUBLIC_HIRING_RE.test(path) || path.startsWith("/hiring/api/");
}

/**
 * Tool / dev surfaces that are NOT modes — they keep their own routes (Screen
 * Inventory §4.3/§4.4). Listed so the resolver can be explicit that returning
 * null for them is intentional coverage, not a gap.
 */
export const NON_MODE_ROOTS: ReadonlySet<string> = new Set([
  "settings",
  "database",
  "help",
  "login",
  "join",
  "onboarding",
  "welcome",
  "styleguide",
  "skeletons",
  "doc-render",
  "products",
  "excellent", // /excellent/now dev view
  "api",
  "search",
  "favorites", // standalone starred-pages page (app/favorites), not a mode
  "notifications", // the Notification Center (app/notifications) — its own page, not the agent decision inbox
]);

/**
 * Workspace-GLOBAL surfaces (the people/orgs Directory, Market intelligence,
 * section Audits) describe the WHOLE workspace, not one project — so they must
 * never render under a project scope (UI-UX-PATTERNS-PRD Issue 24). The exemplar
 * `/projects/quicktest/operations/directory` is a mis-scoped, over-nested path: it
 * resolves the Directory (a workspace surface) under a project + a deep
 * `operations/directory` tail, landing on a generic objects table with no
 * directory context. When one of these appears as a project-scoped section slug OR
 * a deep tail, `resolveSlugRoute` redirects to its canonical WORKSPACE home
 * (dropping the project scope), single-sourced from `CORE_ROUTE`. None of these
 * has a real project-scoped section route (the `[sectionSlug]/audit` route is the
 * singular `audit`, a different slug), so the redirect can't shadow live content.
 */
const WORKSPACE_GLOBAL: ReadonlySet<string> = new Set(["directory", "team", "market", "audits"]);

const SECTION_LANDING = new Set(["overview", "dashboard", ""]);

function viewForRest(rest: string[]): { view?: ModeView; sub?: string } {
  const deep = rest.filter((s) => !SECTION_LANDING.has(s));
  if (deep.length === 0) return {}; // section/stream landing → mode Dashboard
  // Any deep working surface lands on Objects with the original path preserved.
  return { view: "objects", sub: deep.join("/") };
}

/**
 * Resolve a legacy pathname to its mode destination, or `null` when it is not a
 * mode-able route (a tool, dev surface, project home, the workspace home, or an
 * unknown slug). Pure — the caller preserves the original query string.
 */
export function resolveModeRedirect(pathname: string): ModeRedirect | null {
  // Public careers + hiring API are exempt — never rewritten into a mode (§3.3.2).
  if (isPublicHiringPath(pathname)) return null;
  const pathOnly = pathname.split("?")[0] ?? "";
  if (pathOnly === "/personal/api" || pathOnly.startsWith("/personal/api/")) return null;

  const segs = (pathname.split("?")[0] ?? "").split("/").filter(Boolean);
  const root = segs[0];
  if (!root) return null; // workspace home — scope-level, not a mode

  // 3.B: `?view=review` on any Work-absorbed inbox surface resolves to the
  // canonical review door `/agents/verification` (C3) — so /issues?view=review
  // reaches the same place as /verdicts. NB: `/notifications?view=review` is
  // NOT rewritten — Notifications is its own page and owns that tab.
  // Workspace-global: even a scoped /projects/<p>/issues?view=review lands on
  // the one review door (verification is not a project-scoped surface).
  const query = pathname.includes("?") ? pathname.slice(pathname.indexOf("?") + 1) : "";
  if (/(^|&)view=review(&|$)/.test(query)) {
    const REVIEW_DOOR = { area: "verification" } as const;
    if (root === "issues" || root === "tasks" || root === "verdicts") {
      return { mode: "agents", projectSlug: null, extra: { ...REVIEW_DOOR } };
    }
    if (root === "projects" && segs[1] && segs[1] !== "sections" && (segs[2] === "issues" || segs[2] === "tasks")) {
      return { mode: "agents", projectSlug: null, extra: { ...REVIEW_DOOR } };
    }
  }

  // ── /projects/… (section / stream / core, both tiers) ───────────────────
  if (root === "projects") {
    // workspace tier: /projects/sections/<slug>[/rest]
    if (segs[1] === "sections" && segs[2]) {
      return resolveSlugRoute(segs[2], segs.slice(3), null);
    }
    // scoped tier: /projects/<p>/<slug>[/rest]
    if (segs[1] && segs[1] !== "sections" && segs[1] !== "map" && segs[2]) {
      // Already AT a mode root for this scope (/projects/<p>/<mode>, no deeper
      // path) → emit NO redirect. Otherwise the query-less `x-pathname` stamped
      // by proxy.ts re-resolves `area=<mode>` on every pass and the dest never
      // equals the (query-stripped) pathname → an infinite redirect loop. The
      // `operations` mode is the live trigger (its slug is also a section slug).
      if (isModeSlug(segs[2]) && segs.length === 3) return null;
      return resolveSlugRoute(segs[2], segs.slice(3), segs[1]);
    }
    return null; // /projects, /projects/<p> (home), /projects/map — scope surfaces
  }

  // ── top-level core surfaces ─────────────────────────────────────────────
  const core = CORE_ROUTE[root];
  if (core) {
    const { sub } = viewForRest(segs.slice(1));
    const extra: Record<string, string> = {};
    if (core.area) extra.area = core.area;
    // A fixed sub-view (verdicts → area=verification, planner → sub=planner) is
    // the default; a genuine deep-path sub overrides it.
    if (core.sub) extra.sub = core.sub;
    if (sub) extra.sub = sub;
    return { mode: core.mode, projectSlug: null, view: core.view, extra };
  }

  // ── folded product roots ────────────────────────────────────────────────
  const product = PRODUCT_ROUTE[root];
  if (product) {
    const rest = segs.slice(1);
    // `/hiring` now folds into Operations (PRODUCT_ROUTE.hiring → operations): the
    // bare `/hiring` redirects ONCE to `/operations?area=hiring`, and that
    // destination's bare mode root (`/operations`, query stripped by proxy.ts) is
    // in neither PRODUCT_ROUTE nor CORE_ROUTE → resolves to null, closing the loop
    // (the same shape as a folded section like `/finance`). Deep product routes
    // (`/hiring/jobs`) fold in as `?sub=`. The guard below stays as a defensive
    // backstop: were any product slug ALSO a mode slug, its bare root would
    // re-resolve to itself forever — today none are, so it is a no-op.
    if (isModeSlug(root) && rest.length === 0) return null;
    const extra: Record<string, string> = { area: product.area };
    if (rest.length) extra.sub = rest.join("/");
    return { mode: product.mode, projectSlug: null, extra };
  }

  return null; // tools / dev / unknown — keep their own route
}

/** Resolve a section|stream|core slug (+ deep rest) at a tier to a mode dest. */
function resolveSlugRoute(
  slug: string,
  rest: string[],
  projectSlug: string | null,
): ModeRedirect | null {
  // Issue 24: a workspace-global surface reached under a project scope (as the
  // section slug OR anywhere in the deep tail) escapes to its canonical WORKSPACE
  // home — `/projects/<p>/operations/directory` → `/operations?area=team`, never a
  // project-scoped generic table. Single-sourced from CORE_ROUTE so the naming
  // (Team vs directory, Issue 1b) stays consistent with the workspace redirect.
  const global = [slug, ...rest].find((s) => WORKSPACE_GLOBAL.has(s));
  if (global) {
    const core = CORE_ROUTE[global];
    if (core) {
      const extra: Record<string, string> = {};
      if (core.area) extra.area = core.area;
      if (core.sub) extra.sub = core.sub;
      return { mode: core.mode, projectSlug: null, view: core.view, extra };
    }
  }

  const mode: ModeSlug | undefined =
    SECTION_MODE[slug] ??
    STREAM_MODE[slug as StreamSlug] ??
    CORE_ROUTE[slug]?.mode;
  if (!mode) return null; // settings/map/new/unknown under a project — not a mode

  const { view, sub } = viewForRest(rest);
  const extra: Record<string, string> = {};
  // A folded section carries its slug as the Objects sub-area chip.
  if (SECTION_MODE[slug]) extra.area = slug;
  if (sub) extra.sub = sub;
  const coreView = CORE_ROUTE[slug]?.view;
  return { mode, projectSlug, view: view ?? coreView, extra };
}

/** Build the full destination URL from a resolved mode redirect (+ preserved query). */
export function modeRedirectUrl(
  redirect: ModeRedirect,
  extraQuery?: URLSearchParams | Record<string, string>,
): string {
  const extra = { ...redirect.extra };
  if (extraQuery) {
    const it =
      extraQuery instanceof URLSearchParams
        ? extraQuery
        : new URLSearchParams(extraQuery);
    for (const [k, v] of it) if (!(k in extra) && k !== "view") extra[k] = v;
  }
  return modePath(redirect.mode, {
    projectSlug: redirect.projectSlug,
    view: redirect.view,
    extra,
  });
}

/**
 * The nav href for a legacy surface — the resolver above stays pure. A mode-able
 * legacy route resolves to its mode destination; a tool/dev/unknown route is
 * returned byte-for-byte (resolveModeRedirect returns null for those).
 */
export function modeHrefFor(pathname: string): string {
  const r = resolveModeRedirect(pathname);
  return r ? modeRedirectUrl(r) : pathname;
}
